import React, { useState, useEffect } from 'react';
import { X, Tag, BarChart3 } from 'lucide-react';
import { useBusinessId } from '../hooks/useBusinessId';
import { getServiceModifiers, upsertServiceModifiers } from '../lib/serviceTitleTags';
import { toast } from 'react-hot-toast';

interface ModifierModalProps {
  isOpen: boolean;
  onClose: () => void;
  service?: {
    service: {
      id: string;
      name: string;
    };
    priceOption: {
      id: string;
      name: string;
      price: number;
      duration_minutes: number;
    };
  } | null;
}

export default function ModifierModal({ isOpen, onClose, service }: ModifierModalProps) {
  const businessId = useBusinessId();
  const [titleTagEnabled, setTitleTagEnabled] = useState(false);
  const [titleTagText, setTitleTagText] = useState('');
  const [serviceMeterEnabled, setServiceMeterEnabled] = useState(false);
  const [meterTitle, setMeterTitle] = useState('');
  const [meterRating, setMeterRating] = useState(5);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Load existing title tag data when modal opens
  useEffect(() => {
    const loadTitleTagData = async () => {
      if (isOpen && service) {
        setIsLoadingData(true);
        try {
          const existingModifiers = await getServiceModifiers(
            service.service.id
          );
          
          if (existingModifiers) {
            // Load title tag data
            setTitleTagEnabled(existingModifiers.title_tag_enabled);
            setTitleTagText(existingModifiers.title_tag_text || '');
            
            // Load service meter data
            setServiceMeterEnabled(existingModifiers.service_meter_enabled);
            setMeterTitle(existingModifiers.meter_title || '');
            setMeterRating(existingModifiers.meter_rating || 5);
          } else {
            // Reset to defaults
            setTitleTagEnabled(false);
            setTitleTagText('');
            setServiceMeterEnabled(false);
            setMeterTitle('');
            setMeterRating(5);
          }
        } catch (error) {
          console.error('Error loading title tag data:', error);
          setTitleTagEnabled(false);
          setTitleTagText('');
          setServiceMeterEnabled(false);
          setMeterTitle('');
          setMeterRating(5);
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    loadTitleTagData();
  }, [isOpen, service]);

  const handleSave = async () => {
    if (!service || !businessId) return;
    
    setIsLoading(true);
    try {
      await upsertServiceModifiers(
        service.service.id,
        {
          business_id: businessId,
          service_id: service.service.id,
          title_tag_enabled: titleTagEnabled,
          title_tag_text: titleTagEnabled ? titleTagText.trim() : null,
          service_meter_enabled: serviceMeterEnabled,
          meter_title: serviceMeterEnabled ? meterTitle.trim() : null,
          meter_rating: serviceMeterEnabled ? meterRating : null
        }
      );
      
      toast.success('Service modifiers saved successfully!');
      onClose();
    } catch (error) {
      console.error('Error saving service modifiers:', error);
      toast.error('Failed to save service modifiers. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999999] p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl border border-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5 bg-gray-50 rounded-t-2xl">
          <h3 className="text-xl font-bold text-gray-900">
            Service Modifiers
          </h3>
          <button 
            onClick={onClose} 
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-95"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 max-h-[60vh] overflow-y-auto">
          {service && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-1">
                {service.service.name}
              </h4>
              <p className="text-sm text-gray-600">
                {service.priceOption.name} - ${service.priceOption.price}
              </p>
            </div>
          )}
          
          {/* Loading State */}
          {isLoadingData ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            /* Title Tag Modifier */
            <div className="space-y-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Tag className="w-5 h-5 text-blue-600" />
                  <h5 className="font-semibold text-gray-900">Title Tag</h5>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={titleTagEnabled}
                    onChange={(e) => setTitleTagEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
              
              <p className="text-sm text-gray-600 mb-3">
                Add a promotional tag that will be displayed on your service in the customer booking page.
              </p>

              {titleTagEnabled && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Tag Title
                  </label>
                  <input
                    type="text"
                    value={titleTagText}
                    onChange={(e) => setTitleTagText(e.target.value)}
                    placeholder="e.g., Best Deal, Popular, New"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    maxLength={20}
                  />
                  <p className="text-xs text-gray-500">
                    Keep it short and catchy (max 20 characters)
                  </p>
                  
                  {/* Preview */}
                  {titleTagText && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-2">Preview:</p>
                      <div className="inline-flex items-center">
                        <span className="px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded-full">
                          {titleTagText}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Service Meter Modifier */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                  <h5 className="font-semibold text-gray-900">Service Meter</h5>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={serviceMeterEnabled}
                    onChange={(e) => setServiceMeterEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
              
              <p className="text-sm text-gray-600 mb-3">
                Add a visual meter to show the thoroughness level of your service (1-10 scale).
              </p>

              {serviceMeterEnabled && (
                <div className="space-y-4">
                  {/* Meter Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meter Title
                    </label>
                    <input
                      type="text"
                      value={meterTitle}
                      onChange={(e) => setMeterTitle(e.target.value)}
                      placeholder="e.g., Service Thoroughness, Quality Level"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      maxLength={30}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Describe what the meter represents (max 30 characters)
                    </p>
                  </div>

                  {/* Meter Rating */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meter Level (1-10)
                    </label>
                    <div className="space-y-3">
                      {/* Rating Selector */}
                      <div className="flex items-center space-x-2">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                          <button
                            key={rating}
                            type="button"
                            onClick={() => setMeterRating(rating)}
                            className={`w-8 h-8 rounded-full text-sm font-medium transition-all ${
                              meterRating >= rating
                                ? 'bg-purple-600 text-white'
                                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                            }`}
                          >
                            {rating}
                          </button>
                        ))}
                      </div>
                      
                      {/* Current Rating Display */}
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Current Level:</span>
                        <span className="text-sm font-semibold text-purple-600">
                          {meterRating}/10
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Preview */}
                  {meterTitle && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-600 mb-2">Preview:</p>
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-800">{meterTitle}</p>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className="bg-blue-500 h-2.5 rounded-full transition-all duration-300"
                            style={{ width: `${(meterRating / 10) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-200 active:scale-95"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || (titleTagEnabled && !titleTagText.trim()) || (serviceMeterEnabled && !meterTitle.trim())}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
